import { ColDef } from 'ag-grid-community'
import { formatTimestampToHumanReadable, INSTRUMENT_REPORTED_TIMEFORMAT } from 'components/shared/date-utils'
import moment from 'moment'
import { getSamplingStrategyLabel, SamplingStrategy, SessionMetaData } from 'utils/api'
import OpenSessionComponent from './OpenSessionComponent'

interface CellVisualizationTableColumnProps {
  handleOpenSession?: (value: number) => void
}

// Helper function to format a list with intelligent display
const formatListDisplay = (items: string[], maxDisplay = 2): string => {
  if (!items || items.length === 0) return '';
  // Filter out empty/null/undefined values
  const filteredItems = items.filter(item => item && item.trim() !== '');
  if (filteredItems.length === 0) return '';
  if (filteredItems.length <= maxDisplay) return filteredItems.join(', ');
  return `${filteredItems.slice(0, maxDisplay).join(', ')} + ${filteredItems.length - maxDisplay} more`;
};

// Helper function to format sampling strategy
const formatSamplingStrategy = (strategy?: string): string => {
  if (!strategy) return '';
  // Use the common mapping from api.ts
  return getSamplingStrategyLabel(strategy as SamplingStrategy) || strategy;
};

export const getCellVisualizationTableColumnDefs = ({handleOpenSession}: CellVisualizationTableColumnProps): ColDef[] => {
  return [
    {
      headerName: 'Session Name',
      field: 'name',
      sortable: true,
      flex: 1.2,
      minWidth: 140,
      cellStyle: { color: '#5F55D1', borderLeft: '1px solid #EDEDED' },
      tooltipField: 'name',
    },
    {
      headerName: 'Projection',
      field: 'projectionType',
      flex: 0.5,
      minWidth: 80,
    },
    {
      headerName: 'Created By',
      field: 'author_email',
      flex: 1.2,
      minWidth: 150,
      sortable: true,
      tooltipField: 'author_email',
    },
    {
      headerName: 'Created At',
      field: 'created_at',
      flex: 0.9,
      minWidth: 120,
      sortable: true,
      valueGetter: (params) => {
        if (!params.data?.created_at) return '';
        return formatTimestampToHumanReadable(
          moment.utc(params.data.created_at).format(),
          INSTRUMENT_REPORTED_TIMEFORMAT
        );
      },
    },
    {
      headerName: 'Sample IDs',
      field: 'sample_ids',
      flex: 0.8,
      minWidth: 110,
      valueFormatter: (params) => formatListDisplay(params.value, 2),
      cellStyle: { color: '#5F55D1', fontSize: '14px' },
      tooltipValueGetter: (params) => {
        const filtered = params.value?.filter((item: string) => item && item.trim() !== '') || [];
        return filtered.join(', ') || '';
      },
    },
    {
      headerName: 'Sampling',
      field: 'sampling_strategy',
      flex: 0.8,
      minWidth: 100,
      valueFormatter: (params) => formatSamplingStrategy(params.value),
    },
    {
      headerName: 'Run IDs',
      field: 'run_id',
      flex: 1,
      minWidth: 120,
      cellStyle: { color: '#5F55D1', fontSize: '14px' },
      tooltipField: 'run_id',
    },
    {
      headerName: '',
      field: 'open_session',
      flex:1,
      cellRendererFramework: OpenSessionComponent,
      cellRendererParams: {
        handleOpenSession
      },
    },
   ]
}

export type CellVisualizationRowData = {
 sessionId: number
  name: string
  project_code: string
  created_at: Date | string
  updated_at: Date | string
  author_email: string
  run_id: string
  projectionType: string
  sample_ids: string[]
  sample_types: string[]
  sampling_strategy?: string
 }


  export const getCellVisualizationData = (visualizationSessions: SessionMetaData[]): CellVisualizationRowData[] => {
    return visualizationSessions.map((visSession) => {
      const sessionId = visSession.session_id
      const runIds = visSession?.runs?.map((run) => run?.run_id);
      const displayRunIds = runIds?.length > 2
        ? `${runIds?.slice(0, 1).join(', ')} + ${runIds?.length - 1} more`
        : runIds?.join(', ');
      const projectionType =
        visSession.create_spec?.projection_params?.method || 'UMAP'

      // Extract sample IDs and types from runs, filtering out empty/null/undefined values
      const sampleIds = visSession?.runs
        ?.map((run) => run?.sample_id)
        .filter((id) => id && id.trim() !== '') || [];

      const sampleTypes = visSession?.runs
        ?.map((run) => run?.sample_type)
        .filter((type) => type && type.trim() !== '') || [];

      // Get unique values to avoid showing duplicates like "CR3.1, CR3.1"
      const uniqueSampleIds = [...new Set(sampleIds)];
      const uniqueSampleTypes = [...new Set(sampleTypes)];

      const samplingStrategy = visSession.create_spec?.sampling_strategy;

      return {
        sessionId,
        name: visSession.name || 'Unnamed Session',
        project_code: visSession.version_config.projectCode
          ? visSession.version_config.projectCode
          : '-',
        author_email: visSession.author_email || '',
        created_at: visSession.created_at ? visSession.created_at : '',
        updated_at: visSession.updated_at ? visSession.updated_at : '',
        run_id: displayRunIds,
        projectionType,
        sample_ids: uniqueSampleIds,
        sample_types: uniqueSampleTypes,
        sampling_strategy: samplingStrategy,
      }
    })
  }
